/* global vtxWishlistSettings */

(() => {
  'use strict';

  const settings = vtxWishlistSettings;
  const selector = '[data-vtx-wishlist]';
  const pending = new Set();
  let saved = new Set();
  let ready = false;
  let status;
  let statusText;
  let statusClose;

  const announce = (message, error = false) => {
    if (!status) {
      status = document.createElement('div');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      statusText = document.createElement('span');
      statusClose = document.createElement('button');
      statusClose.type = 'button';
      statusClose.className = 'vtx-wishlist-status__close';
      statusClose.setAttribute('aria-label', settings.close);
      statusClose.addEventListener('click', () => announce(''));
      status.append(statusText, statusClose);
      document.body.append(status);
    }
    status.className = error
      ? 'vtx-wishlist-status notice notice--error'
      : 'screen-reader-text';
    statusText.textContent = message;
    statusClose.hidden = !error;
  };

  const sync = () => {
    document.querySelectorAll(selector).forEach((button) => {
      const id = Number(button.dataset.productId);
      const selected = saved.has(id);
      const label = selected ? settings.remove : settings.add;
      button.disabled = !ready || pending.has(id);
      button.setAttribute('aria-busy', pending.has(id) ? 'true' : 'false');
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
      button.querySelector('[data-wishlist-icon]').src = selected
        ? settings.savedIcon
        : settings.emptyIcon;
      const text = button.querySelector('[data-wishlist-label]');
      if (text && text.textContent !== label) {
        text.textContent = label;
      }
    });
  };

  const request = (data) =>
    fetch(settings.ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ wt_nonce: settings.nonce, ...data }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Wishlist request failed');
      }
      return response.json();
    });

  const load = () =>
    request({ action: 'vtx_wishlist_state' })
      .then((response) => {
        if (!response.success || !Array.isArray(response.data.productIds)) {
          throw new Error('Wishlist state unavailable');
        }
        saved = new Set(response.data.productIds.map(Number));
        ready = true;
        sync();
      })
      .catch(() => {
        announce(settings.error, true);
      });

  const start = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest(selector);
      if (!button) {
        return;
      }
      event.preventDefault();
      const id = Number(button.dataset.productId);
      if (!ready || !Number.isInteger(id) || id <= 0 || pending.has(id)) {
        return;
      }

      const remove = saved.has(id);
      pending.add(id);
      sync();
      request({
        action: 'add_to_wishlist',
        product_id: id,
        variation_id: 0,
        quantity: 1,
        act: remove ? 'remove' : 'add',
      })
        .then((response) => {
          if (!response || !response.img_change_url) {
            throw new Error('Wishlist update failed');
          }
          if (remove) {
            saved.delete(id);
          } else {
            saved.add(id);
          }
          announce(remove ? settings.removed : settings.added);
        })
        .catch(() => {
          announce(settings.error, true);
        })
        .finally(() => {
          pending.delete(id);
          sync();
        });
    });

    // Findstr replaces cards after filtering, sorting and pagination.
    new MutationObserver((records) => {
      if (
        records.some((record) =>
          Array.from(record.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.matches(selector) || node.querySelector(selector))
          )
        )
      ) {
        sync();
      }
    }).observe(document.body, { childList: true, subtree: true });

    // The plugin retains its own button and AJAX handler on single-product pages.
    if (window.jQuery) {
      window
        .jQuery(document)
        .on('ajaxSuccess.vtxWishlist', (event, xhr, options, response) => {
          const data = new URLSearchParams(options.data);
          if (
            data.get('action') !== 'add_to_wishlist' ||
            !response ||
            !response.img_change_url
          ) {
            return;
          }
          const id = Number(data.get('product_id'));
          if (data.get('act') === 'remove') {
            saved.delete(id);
          } else {
            saved.add(id);
          }
          sync();
        });
    }

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        load();
      }
    });
    load();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
